//test commit
import * as React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  fetchAllStudents,
  studentApi,
  studentPublicAuth,
  adminStudentImport,
  apiRequest,
  getStudentsWithConsistencyIssues,
  fetchStudentExportOptions,
  fetchStudentsForExport,
  downloadStudentsExportCsv,
  type StudentExportScope,
} from "@/lib/api";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { User } from "@/components/users/UsersTable";
import { AddUserDialog } from "@/components/users/AddUserDialog";
import { EditUserDialog } from "@/components/users/EditUserDialog";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { useDataTable } from "@/hooks/use-data-table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserDetailsSheet } from "@/components/users/UserDetailsSheet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Trash2,
  UserCircle,
  Eye,
  Pen,
  Clock,
  Download,
  AlertTriangle,
  FileDown,
  Search,
  X,
  MoreVertical,
  KeyRound,
  LogIn,
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { can } from "@/utils/permissions";
import config from "@/config";
import { cn } from "@/lib/utils";
import { formatAdminBusinessEt } from "@/lib/admin-datetime";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as XLSX from "xlsx";
import { createLogger } from "@/utils/logger";
import { useApiCall, defaultRetryCondition } from "@/hooks/useApiCall";
import { useDebounce } from "@/hooks/useDebounce";
import type {
  StudentData,
  StudentWithIssues,
  ImportRow,
  ImportValidation,
  ImportProgress,
  ExtendedUser,
} from "@/types/users";

// Create scoped logger for this component
const logger = createLogger("Users");

type UserListSegment = "all" | "students" | "completed" | "signups";

const USER_LIST_TABS: Array<{ id: UserListSegment; label: string }> = [
  { id: "all", label: "All" },
  { id: "students", label: "Students" },
  { id: "completed", label: "Completed" },
  { id: "signups", label: "Signups" },
];

const EMPTY_SEGMENT_COUNTS: Record<UserListSegment, number> = {
  all: 0,
  students: 0,
  completed: 0,
  signups: 0,
};

function isUserListSegment(value: string | null): value is UserListSegment {
  return value === "all" || value === "students" || value === "completed" || value === "signups";
}

function stageLabel(stage: User["stage"]): string {
  if (stage === "students") return "Student";
  if (stage === "completed") return "Completed";
  if (stage === "signups") return "Signup";
  return "—";
}

const mapStudentToUser = (student: StudentData): User => {
  // List API resolves `program`; fall back to programEnrolled / legacy shapes
  let program = "";
  if (student.program && typeof student.program === "string" && student.program.trim() !== "") {
    program = student.program.trim();
  } else if (student.programEnrolled && student.programEnrolled.trim() !== "") {
    program = student.programEnrolled.trim();
  } else if (student.orders && Array.isArray(student.orders) && student.orders.length > 0) {
    const firstOrder = student.orders.find((order) => order.program && order.program.trim() !== "");
    if (firstOrder) program = firstOrder.program.trim();
  } else if (student.courses && Array.isArray(student.courses) && student.courses.length > 0) {
    const firstCourse = student.courses.find((course) => course.program && course.program.trim() !== "");
    if (firstCourse) program = firstCourse.program.trim();
  } else if (student.certification?.programName) {
    program =
      typeof student.certification.programName === "string"
        ? student.certification.programName.trim()
        : "";
  } else if (student.course?.program) {
    program = typeof student.course.program === "string" ? student.course.program.trim() : "";
  } else if (student.order?.program) {
    program = typeof student.order.program === "string" ? student.order.program.trim() : "";
  }

  const name =
    student.fullName ||
    (student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : "") ||
    student.email ||
    "";

  const addressParts = [
    student.address,
    student.city,
    student.state,
    student.postalCode,
    student.country,
  ]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean);

  return {
    id: student._id || student.id || "",
    name,
    email: student.email || "",
    address: addressParts.join(", "),
    cellPhone: student.cellPhoneNumber || "",
    homePhone: "",
    cellPhoneNumber2: "",
    submissionDate: student.createdAt
      ? new Date(student.createdAt).toLocaleDateString()
      : "",
    photoID: { url: "", filename: "" },
    ssnCardCopy: { url: "", filename: "" },
    previousEducation: { url: "", filename: "" },
    program: program || "",
    schedule: "Hybrid Schedule",
    date: student.updatedAt ? new Date(student.updatedAt).toLocaleDateString() : "",
    paymentMethod: "Credit Card",
    agreement: "Agreed",
    ...(student.deliveryMode && { deliveryMode: student.deliveryMode }),
    ...(student.programEnrolled && { programEnrolled: student.programEnrolled }),
    ...(student.dateOfBirth && { dateOfBirth: student.dateOfBirth }),
    ...(student.graduation && { graduation: student.graduation }),
    ...(student.lastLogin && { lastLogin: student.lastLogin }),
    ...(student.lastCertificateDownload && {
      lastCertificateDownload: student.lastCertificateDownload,
    }),
    ...(student.isBlocked !== undefined && { isBlocked: student.isBlocked }),
    ...(student.isDeleted !== undefined && { isDeleted: student.isDeleted }),
    ...(student.stage && { stage: student.stage }),
  } as User;
};

// Helper functions for formatting data
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "";
  return formatAdminBusinessEt(dateString);
};















const Users = () => {
  const { permissions, adminType } = usePermissions();
  const canImpersonate = can(permissions, "users.main.impersonate", adminType);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkUserId = searchParams.get("userId");
  const deepLinkTab = searchParams.get("tab") || undefined;
  const initialSegment = searchParams.get("segment");
  const [segment, setSegment] = useState<UserListSegment>(
    isUserListSegment(initialSegment) ? initialSegment : "all"
  );
  const segmentRef = React.useRef(segment);
  segmentRef.current = segment;
  const [segmentCounts, setSegmentCounts] = useState(EMPTY_SEGMENT_COUNTS);
  const [rows, setRows] = useState<User[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState<number>(-1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isUserDetailsPanelOpen, setIsUserDetailsPanelOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [resendCredentialsTarget, setResendCredentialsTarget] = useState<User | null>(null);
  const [resendingCredentialsForId, setResendingCredentialsForId] = useState<string | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<User | null>(null);
  const [impersonatingForId, setImpersonatingForId] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [deleteProgress, setDeleteProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [processingDelete, setProcessingDelete] = useState(false);
  const [processingAdd, setProcessingAdd] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [fadeInContent, setFadeInContent] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const latestFetchRequestRef = React.useRef(0);
  const hasLoadedUsersRef = React.useRef(false);
  
  // API call hook with retry and cancellation
  const { callApi, cancelAllApiCalls } = useApiCall();
  
  // Debounced search for optimization
  const debouncedSearch = useDebounce(globalSearch, 300);

  // Students with payment issues state
  const [studentsWithIssues, setStudentsWithIssues] = useState<StudentWithIssues[]>([]);
  const [loadingStudentsWithIssues, setLoadingStudentsWithIssues] = useState(false);
  const [showStudentsWithIssues, setShowStudentsWithIssues] = useState(false);
  const [issuesFilter] = useState<{
    issueType?: "all" | "failed_payments" | "overdue_subscriptions" | "payment_method_problems";
    severity?: "high" | "medium" | "low";
  }>({});
  
  // Subscription diagnosis state
  
  // Checkout tracking state

  // New state for enhanced payment features

  // Add new state variables for signature and terms data

  // Add new state for delay fees

  // Import (Excel) state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importValidation, setImportValidation] = useState<ImportValidation>({
    total: 0,
    valid: 0,
    invalid: 0,
    errors: [],
  });

  // Export dialog state
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState<StudentExportScope>("all");
  const [exportProgram, setExportProgram] = useState("");
  const [exportFormat, setExportFormat] = useState<"csv" | "excel">("csv");
  const [exportPrograms, setExportPrograms] = useState<string[]>([]);
  const [exportProgramsLoading, setExportProgramsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress>({
    current: 0,
    total: 0,
    created: 0,
    existed: 0,
    failed: 0,
  });
  const [useBulkEndpoint, setUseBulkEndpoint] = useState(false);
  const validImportRows = React.useMemo(() => {
    const invalid = new Set(importValidation.errors.map((e) => e.index));
    return importRows.filter((_, idx) => !invalid.has(idx));
  }, [importRows, importValidation]);

  // Add animation trigger effects
  useEffect(() => {
    if (selectedUsers.length > 0) {
      setShowDeleteButton(true);
    } else {
      setTimeout(() => setShowDeleteButton(false), 200);
    }
  }, [selectedUsers]);

  useEffect(() => {
    if (!isInitialLoading && !isTableLoading && !error) {
      setFadeInContent(true);
    }
  }, [isInitialLoading, isTableLoading, error]);

  const fetchUsers = React.useCallback(
    async (params?: Record<string, string | number>, signal?: AbortSignal) => {
      const requestId = latestFetchRequestRef.current + 1;
      latestFetchRequestRef.current = requestId;
      const shouldShowInitialLoading = !hasLoadedUsersRef.current && rows.length === 0;

      if (shouldShowInitialLoading) {
        setIsInitialLoading(true);
      } else {
        setIsTableLoading(true);
      }
      setError(null);
      
      try {
        const response = await callApi(
          'fetchUsers',
          async () => {
            return await fetchAllStudents({
              page: (params?.page as number) || 1,
              perPage: (params?.perPage as number) || 20,
              search: params?.search as string,
              sortBy: params?.sortBy as string,
              sortOrder: (params?.sortOrder as "asc" | "desc") || "asc",
              active: true,
              type: 'students',
              segment: segmentRef.current,
            });
          },
          {
            maxRetries: 3,
            retryDelay: 1000,
            retryCondition: defaultRetryCondition,
            signal,
          }
        );
        
        // Handle new pagination response format
        const responseData = response?.data as {
          users?: StudentData[];
          students?: StudentData[];
          pagination?: {
            total?: number;
            totalPages?: number;
            pages?: number;
          };
          summary?: {
            segments?: Partial<Record<UserListSegment, number>>;
          };
        };
        
        const students = responseData?.users || responseData?.students || [];
        
        if (!Array.isArray(students)) {
          logger.error('Students data is not an array', { students });
          setRows([]);
          setError('Invalid response format from server');
          return;
        }
        
        // Extract pagination info from data.pagination
        const pagination = responseData?.pagination;
        const count = pagination?.total ?? 0;
        const perPage = (params?.perPage as number) || 20;
        const totalPages = pagination?.totalPages ?? pagination?.pages ?? Math.ceil(count / perPage);
        
        setRows(students.map(mapStudentToUser));
        setTotalCount(count);
        setPageCount(count > 0 ? totalPages : -1);
        const segments = responseData?.summary?.segments;
        if (segments) {
          setSegmentCounts({
            all: segments.all ?? 0,
            students: segments.students ?? 0,
            completed: segments.completed ?? 0,
            signups: segments.signups ?? 0,
          });
        }
        hasLoadedUsersRef.current = true;
      } catch (err) {
        // Don't show error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch students";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        if (latestFetchRequestRef.current === requestId) {
          setIsInitialLoading(false);
          setIsTableLoading(false);
        }
      }
    },
    [callApi, rows.length]
  );


  // Fetch detailed user information




















  const handleViewUserDetails = (user: User) => {
    logger.debug("Viewing user details", { userId: user.id, email: user.email });
    setCurrentUser(user);
    setIsUserDetailsPanelOpen(true);
    // <UserDetailsSheet /> owns the data fetch via its own useEffect.
  };

  // Deep-link from notifications: /users?userId=...&tab=documents|payments
  useEffect(() => {
    if (!deepLinkUserId) return;
    setCurrentUser({
      id: deepLinkUserId,
      name: "",
      email: "",
      address: "",
      cellPhone: "",
      homePhone: "",
      cellPhoneNumber2: "",
      submissionDate: "",
      photoID: { url: "", filename: "" },
      ssnCardCopy: { url: "", filename: "" },
      previousEducation: { url: "", filename: "" },
      program: "",
      schedule: "Hybrid Schedule",
      date: "",
      paymentMethod: "Credit Card",
      agreement: "Agreed",
    });
    setIsUserDetailsPanelOpen(true);
  }, [deepLinkUserId]);

  const confirmResendCredentials = (user: User) => {
    setResendCredentialsTarget(user);
  };

  const handleViewAsUser = async () => {
    const user = impersonateTarget;
    if (!user) return;

    if (!config.studentAppUrl) {
      toast.error("Student portal URL is not configured (VITE_STUDENT_APP_URL).");
      setImpersonateTarget(null);
      return;
    }

    setImpersonatingForId(user.id);
    try {
      const res = await studentApi.impersonate(user.id);
      const baseUrl = res?.data?.impersonationUrl;
      if (!baseUrl) {
        throw new Error("No impersonation URL returned");
      }
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const url = new URL(baseUrl);
      url.searchParams.set("returnTo", returnTo);
      url.searchParams.set("sessionMode", "tab");
      window.open(url.toString(), "_blank", "noopener,noreferrer");
      toast.success(`Opened student portal as ${user.name || user.email}`);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Failed to start impersonation";
      toast.error(message);
    } finally {
      setImpersonatingForId(null);
      setImpersonateTarget(null);
    }
  };

  const handleResendCredentials = async () => {
    const user = resendCredentialsTarget;
    if (!user) return;

    setResendingCredentialsForId(user.id);
    try {
      const res = await apiRequest<{
        statusCode: number;
        message: string;
        data: { studentId: string; email: string; requiresPasswordReset: boolean };
      }>(`/api/admin/users/students/${user.id}/resend-credentials`, { method: "POST" });
      const email = res?.data?.email || user.email;
      toast.success(`Credentials email resent to ${email}`);

      // If the sidebar is open for this user, we don't need to refetch here —
      // <UserDetailsSheet /> picks up the new state next time it opens.
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend credentials. Try again.");
    } finally {
      setResendingCredentialsForId(null);
      setResendCredentialsTarget(null);
    }
  };

  const handleEditUser = React.useCallback((user: User) => {
    setCurrentUser(user);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenAddUser = React.useCallback(() => {
    setIsAddDialogOpen(true);
  }, []);

  const handleDeleteUser = (user: User) => {
    setCurrentUser(user);
    setIsDeleteDialogOpen(true);
  };

  const columns = React.useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "name",
        accessorKey: "name",
        meta: { label: "Name" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Name" sortVariant="alphabetical" />
        ),
        cell: ({ row }) => (
          <button
            type="button"
            className="text-primary underline underline-offset-2 hover:no-underline"
            onClick={() => handleViewUserDetails(row.original)}
          >
            {row.original.name}
          </button>
        ),
      },
      {
        id: "stage",
        accessorKey: "stage",
        meta: { label: "Stage" },
        enableSorting: false,
        header: () => <span>Stage</span>,
        cell: ({ row }) => {
          const stage = row.original.stage;
          return (
            <Badge
              variant={stage === "signups" ? "outline" : stage === "completed" ? "secondary" : "default"}
            >
              {stageLabel(stage)}
            </Badge>
          );
        },
      },
      {
        id: "email",
        accessorKey: "email",
        meta: { label: "Email" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Email" sortVariant="alphabetical" />
        ),
      },
      {
        id: "submissionDate",
        accessorKey: "submissionDate",
        meta: { label: "Submission Date" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Submission Date" sortVariant="chronological" />
        ),
      },
      {
        id: "program",
        accessorKey: "program",
        meta: { label: "Program" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Program" sortVariant="alphabetical" />
        ),
        cell: ({ row }) => {
          const extendedUser = row.original as ExtendedUser;
          const programName = row.original.program || extendedUser.programEnrolled;
          return (
            <span className="text-sm">
              {programName || "-"}
            </span>
          );
        },
      },
      {
        id: "cellPhone",
        accessorKey: "cellPhone",
        meta: { label: "Cell Phone" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Cell Phone" sortVariant="alphabetical" />
        ),
      },
      {
        id: "deliveryMode",
        accessorKey: "deliveryMode",
        meta: { label: "Delivery Mode" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Delivery Mode" sortVariant="alphabetical" />
        ),
        cell: ({ row }) => {
          const extendedUser = row.original as ExtendedUser;
          const mode = extendedUser.deliveryMode;
          return mode ? (
            <Badge variant="outline" className="text-xs">
              {mode}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">-</span>
          );
        },
      },
      {
        id: "dateOfBirth",
        accessorKey: "dateOfBirth",
        meta: { label: "Date of Birth" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Date of Birth" sortVariant="chronological" />
        ),
        cell: ({ row }) => {
          const extendedUser = row.original as ExtendedUser;
          const dob = extendedUser.dateOfBirth;
          return (
            <span className="text-sm">
              {dob ? new Date(dob).toLocaleDateString() : "-"}
            </span>
          );
        },
      },
      {
        id: "graduation",
        accessorKey: "graduation",
        meta: { label: "Graduation" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Graduation" sortVariant="alphabetical" />
        ),
        cell: ({ row }) => {
          const graduation = (row.original as User & { graduation?: { month?: string; year?: string } }).graduation;
          if (graduation && (graduation.month || graduation.year)) {
            return (
              <span className="text-sm">
                {graduation.month && graduation.year
                  ? `${graduation.month} ${graduation.year}`
                  : graduation.month || graduation.year || "-"}
              </span>
            );
          }
          return <span className="text-sm text-muted-foreground">-</span>;
        },
      },
      {
        id: "lastLogin",
        accessorKey: "lastLogin",
        meta: { label: "Last Login" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Last Login" sortVariant="chronological" />
        ),
        cell: ({ row }) => {
          const lastLogin = (row.original as User & { lastLogin?: string }).lastLogin;
          return (
            <span className="text-sm">
              {lastLogin ? formatDate(lastLogin) : "Never"}
            </span>
          );
        },
      },
      {
        id: "lastCertificateDownload",
        accessorKey: "lastCertificateDownload",
        meta: { label: "Last Certificate Download" },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Last Certificate Download" sortVariant="chronological" />
        ),
        cell: ({ row }) => {
          const lastDownload = (row.original as User & { lastCertificateDownload?: string | null }).lastCertificateDownload;
          return (
            <span className="text-sm">
              {lastDownload ? formatDate(lastDownload) : "Never"}
            </span>
          );
        },
      },
      {
        id: "actions",
        meta: { label: "Actions" },
        header: () => (
          <span className="sr-only">Actions</span>
        ),
        size: 56,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => handleViewUserDetails(row.original)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEditUser(row.original)}>
                  <Pen className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                {canImpersonate && (
                  <DropdownMenuItem
                    onClick={() => setImpersonateTarget(row.original)}
                    disabled={impersonatingForId === row.original.id}
                  >
                    {impersonatingForId === row.original.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <LogIn className="h-4 w-4 mr-2" />
                    )}
                    View as user
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => confirmResendCredentials(row.original)}
                  disabled={resendingCredentialsForId === row.original.id}
                >
                  {resendingCredentialsForId === row.original.id ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4 mr-2" />
                  )}
                  Resend credentials
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleDeleteUser(row.original)} variant="destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [handleViewUserDetails, handleEditUser, handleDeleteUser, canImpersonate, impersonatingForId]
  );

  const { table } = useDataTable({
    data: rows,
    columns,
    pageCount: pageCount,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      columnVisibility: {
        graduation: false,
        lastCertificateDownload: false,
      },
    },
    enableRowSelection: true,
  });

  // Memoize table state values to prevent unnecessary re-renders
  const tableState = table.getState();
  const pageIndex = tableState.pagination.pageIndex;
  const pageSize = tableState.pagination.pageSize;
  const sorting = tableState.sorting;
  const columnFilters = tableState.columnFilters;
  
  // Memoize sorting and filters as strings for comparison
  const sortingKey = React.useMemo(() => JSON.stringify(sorting), [sorting]);
  const filtersKey = React.useMemo(() => JSON.stringify(columnFilters), [columnFilters]);

  // Build params function - not memoized to avoid dependency issues
  const buildParamsFromTableState = React.useCallback(() => {
    const state = table.getState();
    const { pagination, sorting, columnFilters } = state;

    const params: Record<string, string | number> = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
    };

    if (sorting.length > 0) {
      const sort = sorting[0];
      params.sortBy = sort.id;
      params.sortOrder = sort.desc ? "desc" : "asc";
    }

    columnFilters.forEach((filter) => {
      if (filter.value) {
        const value = Array.isArray(filter.value)
          ? filter.value.join(",")
          : String(filter.value);
        if (value) {
          params[filter.id] = value;
        }
      }
    });

    return params;
  }, []); // No dependencies - we'll get fresh state when called

  // Single effect to handle all table state changes and search
  React.useEffect(() => {
    const abortController = new AbortController();
    
    // Build params from current table state
    const state = table.getState();
    const { pagination, sorting, columnFilters } = state;
    
    const params: Record<string, string | number> = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
    };

    if (sorting.length > 0) {
      const sort = sorting[0];
      params.sortBy = sort.id;
      params.sortOrder = sort.desc ? "desc" : "asc";
    }

    columnFilters.forEach((filter) => {
      if (filter.value) {
        const value = Array.isArray(filter.value)
          ? filter.value.join(",")
          : String(filter.value);
        if (value) {
          params[filter.id] = value;
        }
      }
    });

    // Use debounced search value
    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }
    
    fetchUsers(params, abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [pageIndex, pageSize, sortingKey, filtersKey, debouncedSearch, segment, fetchUsers]);
  
  // Separate effect to reset page when search changes (but don't fetch yet - let debounce handle it)
  React.useEffect(() => {
    if (debouncedSearch !== "" && debouncedSearch !== globalSearch) {
      table.setPageIndex(0);
    }
  }, [debouncedSearch, globalSearch, table]);
  
  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      cancelAllApiCalls();
    };
  }, [cancelAllApiCalls]);

  // Sync table row selection with selectedUsers state
  React.useEffect(() => {
    const rowSelection = table.getState().rowSelection;
    const selectedIds = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => {
        const row = table.getRow(key);
        return row?.original?.id;
      })
      .filter(Boolean) as string[];
    
    // Only update if selection actually changed
    if (JSON.stringify(selectedIds.sort()) !== JSON.stringify(selectedUsers.sort())) {
      setSelectedUsers(selectedIds);
    }
  }, [table.getState().rowSelection]); // Remove table from deps - only watch rowSelection

  const refetchUsers = React.useCallback(() => {
    // Get fresh state when called, don't depend on buildParamsFromTableState
    const state = table.getState();
    const { pagination, sorting, columnFilters } = state;
    
    const params: Record<string, string | number> = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
    };

    if (sorting.length > 0) {
      const sort = sorting[0];
      params.sortBy = sort.id;
      params.sortOrder = sort.desc ? "desc" : "asc";
    }

    columnFilters.forEach((filter) => {
      if (filter.value) {
        const value = Array.isArray(filter.value)
          ? filter.value.join(",")
          : String(filter.value);
        if (value) {
          params[filter.id] = value;
        }
      }
    });

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }
    
    fetchUsers(params);
  }, [fetchUsers, debouncedSearch, table]);

  const loadStudentsWithIssues = React.useCallback(async () => {
    setLoadingStudentsWithIssues(true);
    try {
      const response = await getStudentsWithConsistencyIssues({
        ...issuesFilter,
        limit: 50,
        page: 1,
      });
      setStudentsWithIssues((response.data.students || []).map((student: any) => ({
        ...student,
        issues: student.issues?.map((issue: any) => ({
          ...issue,
          severity: (issue.severity === 'high' || issue.severity === 'medium' || issue.severity === 'low') 
            ? issue.severity 
            : 'low' as 'high' | 'medium' | 'low'
        })) || []
      })));
    } catch (error) {
      logger.error(
        error instanceof Error ? error : new Error("Failed to load students with issues"),
        { issuesFilter }
      );
      toast.error("Failed to load students with payment issues");
    } finally {
      setLoadingStudentsWithIssues(false);
    }
  }, [issuesFilter]);

  useEffect(() => {
    if (showStudentsWithIssues) {
      loadStudentsWithIssues();
    }
  }, [showStudentsWithIssues, loadStudentsWithIssues]);






  // Subscription management handlers






  const handleCreateUserFromDialog = React.useCallback(async (formData: any) => {
    if (!formData.email || !formData.fullName || !formData.cellPhoneNumber) {
      toast.error("Please fill required fields");
      return;
    }
    setProcessingAdd(true);
    try {
      const phoneNumber = formData.cellPhoneNumber.trim();
      if (!phoneNumber || !/^\+[1-9]\d{1,14}$/.test(phoneNumber)) {
        toast.error("Invalid phone number. Must be in E.164 format (e.g., +12125551234)");
        setProcessingAdd(false);
        return;
      }
      const payload: any = {
        email: formData.email.trim(),
        cellPhoneNumber: phoneNumber,
        programEnrolled: formData.programEnrolled.trim(),
        fullName: formData.fullName.trim(),
        dateOfBirth: formData.dateOfBirth,
        graduation: {
          month: formData.graduationMonth,
          year: formData.graduationYear,
        },
        deliveryMode: formData.deliveryMode || null,
      };
      await apiRequest(`/api/admin/users/students`, { method: "POST", body: payload });
      toast.success("User created");
      setIsAddDialogOpen(false);
      setFadeInContent(false);
      refetchUsers();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to create user";
      toast.error(msg);
    } finally {
      setProcessingAdd(false);
    }
  }, [refetchUsers]);

  const handleConfirmDelete = async () => {
    if (!currentUser) return;

    setProcessingDelete(true);
    try {
      await studentApi.deleteStudent(currentUser.id);
      toast.success("User deleted successfully");
      setIsDeleteDialogOpen(false);
      setFadeInContent(false);
      refetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setProcessingDelete(false);
    }
  };


  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) return;
    setIsBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedUsers.length === 0) return;

    setIsTableLoading(true);
    setDeleteProgress({ current: 0, total: selectedUsers.length });
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedUsers.length; i++) {
      const userId = selectedUsers[i];
      try {
        await studentApi.deleteStudent(userId);
        successCount++;
      } catch (error) {
        failCount++;
        logger.error(
          error instanceof Error ? error : new Error(`Failed to delete user with ID ${userId}`),
          { userId }
        );
      }
      setDeleteProgress({ current: i + 1, total: selectedUsers.length });

      // Add a small delay for a smoother progress bar animation
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (successCount > 0) {
      toast.success(`Successfully deleted ${successCount} users`);
    }

    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} users`);
    }

    setSelectedUsers([]);
    setIsBulkDeleteDialogOpen(false);
    setDeleteProgress(null);
    setFadeInContent(false);
    const params = buildParamsFromTableState();
    try {
      await fetchUsers(params);
    } finally {
      setIsTableLoading(false);
    }
  };

  // ==== Import helpers ====
  const generateStrongPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";
    const specials = "!@#$%^&*()-_=+[]{};:,.<>?";
    const all = upper + lower + digits + specials;
    const pick = (chars: string) =>
      chars[Math.floor(Math.random() * chars.length)];
    const base = [pick(upper), pick(lower), pick(digits), pick(specials)];
    while (base.length < 12) base.push(pick(all));
    // shuffle
    for (let i = base.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [base[i], base[j]] = [base[j], base[i]];
    }
    return base.join("");
  };

  const isValidE164 = (phone: string): boolean => {
    return /^\+[1-9]\d{1,14}$/.test((phone || "").trim());
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "First Name",
      "Last Name",
      "DOB (yyyy-mm-dd)",
      "Phone Number",
      "Email",
      "Program Name",
      "Start Date (yyyy-mm-dd)",
      "Plan Type (FULL | BI-WEEKLY | MONTHLY)",
      "Amount",
      "Amount Paid",
      "Amount Remaining",
    ];
    const sample = [
      "John",
      "Doe",
      "1992-05-20",
      "2125551234",
      "john.doe@example.com",
      "Certified Nursing Assistant (CNA)",
      "2025-09-15",
      "MONTHLY",
      2000,
      500,
      1500,
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, sample]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Users Template");
    XLSX.writeFile(wb, "bulk_user_import_template.xlsx");
  };

  const normalizeHeaderKey = (key: string) =>
    key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const mapRow = (row: any) => {
    const map: Record<string, string> = {};
    Object.keys(row || {}).forEach((k) => {
      map[normalizeHeaderKey(k)] = row[k];
    });
    const firstName = map["firstname"] || map["first"] || "";
    const lastName = map["lastname"] || map["last"] || "";
    const email = (map["email"] || "").toString().trim().toLowerCase();
    const cell =
      map["cellphonenumber"] ||
      map["phonenumber"] ||
      map["phone"] ||
      map["cell"] ||
      "";
    const password = map["password"] || "";
    // Bulk-import optional fields
    const dob = map["dob"] || map["dateofbirth"] || "";
    const programName = map["programname"] || map["program"] || "";
    const startDate = map["startdate"] || "";
    const planType = map["plantype"] || "";
    const amount = map["amount"] || "";
    const amountPaid = map["amountpaid"] || "";
    const amountRemaining = map["amountremaining"] || "";
    return {
      firstName: String(firstName).trim(),
      lastName: String(lastName).trim(),
      email,
      cellPhoneNumber: String(cell).trim(),
      password: password ? String(password) : undefined,
      dob: dob ? String(dob) : undefined,
      programName: programName ? String(programName) : undefined,
      startDate: startDate ? String(startDate) : undefined,
      planType: planType ? String(planType) : undefined,
      amount: amount !== "" ? Number(amount) : undefined,
      amountPaid: amountPaid !== "" ? Number(amountPaid) : undefined,
      amountRemaining:
        amountRemaining !== "" ? Number(amountRemaining) : undefined,
      _raw: row,
    };
  };

  const validateRows = (
    rows: Array<{
      firstName: string;
      lastName: string;
      email: string;
      cellPhoneNumber: string;
      password?: string;
      dob?: string;
      programName?: string;
      startDate?: string;
      planType?: string;
      amount?: number;
      amountPaid?: number;
      amountRemaining?: number;
      _raw?: any;
    }>
  ) => {
    const errors: Array<{ index: number; email?: string; reasons: string[] }> =
      [];
    let valid = 0;
    rows.forEach((r, idx) => {
      const reasons: string[] = [];
      if (!r.firstName || r.firstName.length < 1)
        reasons.push("firstName required");
      if (!r.lastName || r.lastName.length < 1)
        reasons.push("lastName required");
      if (!r.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email))
        reasons.push("invalid email");
      if (!isValidE164(r.cellPhoneNumber)) reasons.push("invalid cellPhoneNumber (E.164 required)");
      if (
        r.password &&
        (r.password.length < 8 ||
          r.password.length > 128 ||
          !/[A-Z]/.test(r.password) ||
          !/[a-z]/.test(r.password) ||
          !/\d/.test(r.password) ||
          !/[!@#$%^&*()\-_=+\[\]{};:,.<>?]/.test(r.password))
      ) {
        reasons.push("password does not meet complexity");
      }
      // If bulk mode is chosen, validate bulk fields when present
      if (useBulkEndpoint) {
        if (!r.programName) reasons.push("programName required (bulk)");
        if (!r.startDate) reasons.push("startDate required (bulk yyyy-mm-dd)");
        if (
          !r.planType ||
          !["FULL", "BI-WEEKLY", "MONTHLY"].includes(r.planType.toUpperCase())
        )
          reasons.push("planType must be FULL | BI-WEEKLY | MONTHLY");
        if (r.amount === undefined || isNaN(r.amount) || r.amount < 0)
          reasons.push("amount must be a number >= 0");
        if (
          r.amountPaid === undefined ||
          isNaN(r.amountPaid) ||
          r.amountPaid < 0
        )
          reasons.push("amountPaid must be a number >= 0");
        if (
          r.amountRemaining === undefined ||
          isNaN(r.amountRemaining) ||
          r.amountRemaining < 0
        )
          reasons.push("amountRemaining must be a number >= 0");
        if (
          r.amount !== undefined &&
          r.amountPaid !== undefined &&
          r.amountRemaining !== undefined
        ) {
          const total = Number(r.amountPaid) + Number(r.amountRemaining);
          if (Math.abs(total - Number(r.amount)) > 0.01) {
            reasons.push("amountPaid + amountRemaining must equal amount");
          }
        }
      }
      if (reasons.length === 0) valid++;
      else errors.push({ index: idx, email: r.email, reasons });
    });
    setImportValidation({
      total: rows.length,
      valid,
      invalid: rows.length - valid,
      errors,
    });
  };

  const handleImportFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;
    setImportRows([]);
    setImportValidation({ total: 0, valid: 0, invalid: 0, errors: [] });
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" }) as Record<string, unknown>[];
      const rows = json.map(mapRow);
      setImportRows(rows);
      validateRows(rows);
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error("Excel parse error"),
        { fileName: file.name }
      );
      toast.error("Failed to read Excel file");
    }
  };

  const handleStartImport = async () => {
    if (validImportRows.length === 0) return;
    setImportInProgress(true);

    // Bulk admin endpoint flow
    if (useBulkEndpoint) {
      const importId = `batch_${Date.now()}`;
      try {
        const payload = {
          importId,
          users: validImportRows.map((r) => ({
            firstName: r.firstName,
            lastName: r.lastName,
            dob: r.dob,
            phoneNumber: r.cellPhoneNumber,
            email: r.email,
            programName: r.programName!,
            startDate: r.startDate!,
            planType: r.planType!.toUpperCase() as
              | "FULL"
              | "BI-WEEKLY"
              | "MONTHLY",
            amount: Number(r.amount!),
            amountPaid: Number(r.amountPaid!),
            amountRemaining: Number(r.amountRemaining!),
          })),
        };
        const res = await adminStudentImport.bulkImport(payload);
        const bulkResponse = res as {
          summary?: { success: number; failed: number; skipped: number };
          results?: Array<{ status: string }>;
        };
        const { summary, results } = bulkResponse;
        if (summary) {
          toast.success(
            `Bulk import completed. Success: ${summary.success}, Failed: ${summary.failed}, Skipped: ${summary.skipped}`
          );
        }
        // Optionally surface first few errors
        const failed = (results || []).filter(
          (r: any) => r.status === "failed"
        );
        if (failed.length) {
          logger.warn("Bulk import failures", {
            failedCount: failed.length,
            failures: failed.slice(0, 5),
          });
          toast.warning(
            `${failed.length} row(s) failed. Check logs for details.`
          );
        }
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error("Bulk import error"),
          { importId }
        );
        toast.error("Bulk import failed");
      } finally {
        setImportInProgress(false);
        setIsImportDialogOpen(false);
        setImportRows([]);
        setImportValidation({ total: 0, valid: 0, invalid: 0, errors: [] });
        refetchUsers();
      }
      return;
    }

    // Legacy per-user flow
    setImportProgress({
      current: 0,
      total: validImportRows.length,
      created: 0,
      existed: 0,
      failed: 0,
    });
    let created = 0,
      existed = 0,
      failed = 0;

    for (let i = 0; i < validImportRows.length; i++) {
      const row = validImportRows[i];
      try {
        const password =
          row.password && row.password.length >= 8
            ? row.password
            : generateStrongPassword();
        const res = await studentPublicAuth.register({
          email: row.email,
          firstName: row.firstName,
          lastName: row.lastName,
          cellPhoneNumber: row.cellPhoneNumber,
          password,
        });
        const registerResponse = res as { message?: string };
        const msg = registerResponse?.message || "";
        if (/welcome back/i.test(msg)) {
          existed++;
        } else {
          created++;
        }
      } catch (error) {
        failed++;
      }
      setImportProgress({
        current: i + 1,
        total: validImportRows.length,
        created,
        existed,
        failed,
      });
    }

    setImportInProgress(false);
    toast.success(
      `Import completed. Created: ${created}, Existing: ${existed}, Failed: ${failed}`
    );
    setIsImportDialogOpen(false);
    setImportRows([]);
    setImportValidation({ total: 0, valid: 0, invalid: 0, errors: [] });
      refetchUsers();
  };


  // New enhanced export functions using react-csv and react-to-pdf


  // Export individual invoice/receipt using react-to-pdf


  // Export all invoices/receipts


  const openExportDialog = React.useCallback(() => {
    setIsExportDialogOpen(true);
    if (exportPrograms.length > 0) return;
    setExportProgramsLoading(true);
    void fetchStudentExportOptions()
      .then((programs) => setExportPrograms(programs))
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : "Failed to load programs");
      })
      .finally(() => setExportProgramsLoading(false));
  }, [exportPrograms.length]);

  const handleExportStudents = React.useCallback(async () => {
    if (exportScope === "program" && !exportProgram.trim()) {
      toast.error("Select a program to export");
      return;
    }

    setExporting(true);
    const scopeLabel =
      exportScope === "program"
        ? `program_${exportProgram.replace(/[^a-zA-Z0-9]+/g, "_").slice(0, 40)}`
        : exportScope;
    const filename = `students_${scopeLabel}_${new Date().toISOString().slice(0, 10)}`;

    try {
      if (exportFormat === "csv") {
        const blob = await downloadStudentsExportCsv({
          scope: exportScope,
          program: exportProgram || undefined,
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Students exported as CSV");
      } else {
        const { students, count, truncated } = await fetchStudentsForExport({
          scope: exportScope,
          program: exportProgram || undefined,
        });
        if (count === 0) {
          toast.error("No students matched this filter");
          return;
        }
        const worksheet = XLSX.utils.json_to_sheet(
          students.map((row) => ({
            "Account ID": row.accountId,
            Name: row.fullName,
            Email: row.email,
            "Cell Phone": row.cellPhoneNumber,
            Address: row.address,
            City: row.city,
            State: row.state,
            "Postal Code": row.postalCode,
            Country: row.country,
            Program: row.program,
            "Delivery Mode": row.deliveryMode,
            "Date of Birth": row.dateOfBirth,
            Graduation: row.graduation,
            "Last Login": row.lastLogin,
            "Created At": row.createdAt,
            Blocked: row.isBlocked,
          }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
        XLSX.writeFile(workbook, `${filename}.xlsx`);
        toast.success(
          truncated
            ? `Exported first ${count} students (limit reached)`
            : `Exported ${count} students as Excel`
        );
      }
      setIsExportDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }, [exportFormat, exportProgram, exportScope]);

  const handleSegmentChange = (value: string) => {
    if (!isUserListSegment(value) || value === segment) return;
    setSegment(value);
    table.setPageIndex(0);
    table.resetRowSelection();
    setSelectedUsers([]);
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("segment");
    else next.set("segment", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="container mx-auto py-8 px-4">
      {showStudentsWithIssues && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Students with Payment Issues</CardTitle>
              <CardDescription>
                Students who have payment problems requiring attention
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setShowStudentsWithIssues(false);
                setStudentsWithIssues([]);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </CardHeader>
          <CardContent>
            {loadingStudentsWithIssues ? (
              <TableSkeleton columns={5} rows={5} />
            ) : studentsWithIssues.length > 0 ? (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Issues</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentsWithIssues.map((student) => (
                      <TableRow key={student._id}>
                        <TableCell>
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {student.issues?.map((issue: any, idx: number) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className={
                                  issue.severity === "high"
                                    ? "border-red-500 text-red-700"
                                    : issue.severity === "medium"
                                    ? "border-yellow-500 text-yellow-700"
                                    : "border-gray-500 text-gray-700"
                                }
                              >
                                {issue.type} ({issue.severity})
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(student.paymentSummary?.totalOutstanding || 0)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const user = rows.find((r) => r.id === student._id);
                              if (user) {
                                handleViewUserDetails(user);
                                setShowStudentsWithIssues(false);
                              }
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No students with payment issues found
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex-none">
          <h1 className="whitespace-nowrap text-2xl font-bold leading-tight">User Management</h1>
        </div>
        <div className="flex flex-1 flex-wrap items-center justify-start gap-2 sm:justify-end">
          {/* Add User Button */}
          <Button
            onClick={handleOpenAddUser}
            className="flex items-center gap-2"
            size="default"
          >
            <UserCircle className="h-4 w-4" />
            Add User
          </Button>
          {/* Import Users Button */}
          <Button
            variant="outline"
            onClick={() => setIsImportDialogOpen(true)}
            className="flex items-center gap-2"
            size="default"
          >
            <Download className="h-4 w-4 rotate-180" />
            Import Users
          </Button>
          {/* Export Users Button */}
          <Button
            variant="outline"
            onClick={openExportDialog}
            className="flex items-center gap-2"
            size="default"
          >
            <Download className="h-4 w-4" />
            Export Users
          </Button>
          {/* Students with Issues Button */}
          <Button
            variant="outline"
            onClick={() => setShowStudentsWithIssues(!showStudentsWithIssues)}
            className="flex items-center gap-2"
            size="default"
          >
            <AlertTriangle className="h-4 w-4" />
            {showStudentsWithIssues ? "Hide Issues" : "Students with Issues"}
          </Button>
          {/* Refresh Button */}
          <Button
            variant="outline"
            onClick={refetchUsers}
            disabled={isInitialLoading || isTableLoading}
            className="flex items-center gap-2"
            size="default"
          >
            {isInitialLoading || isTableLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <Clock className="h-4 w-4" />
                Refresh
              </>
            )}
          </Button>
          {/* Delete Selected Button */}
          <div
            className={cn(
              "transition-all duration-300 ease-in-out transform",
              showDeleteButton
                ? "opacity-100 scale-100 translate-x-0"
                : "hidden"
            )}
          >
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              className="flex items-center gap-2"
              size="default"
            >
              <Trash2 className="h-4 w-4" />
              Delete Selected ({selectedUsers.length})
            </Button>
          </div>
        </div>
      </div>

      <Tabs value={segment} onValueChange={handleSegmentChange} className="mb-4">
        <TabsList>
          {USER_LIST_TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="gap-1.5">
              {tab.label}
              <span className="text-muted-foreground tabular-nums">
                {segmentCounts[tab.id]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isInitialLoading ? (
        <div className="space-y-4">
          <TableSkeleton columns={7} rows={5} />
          <div className="flex justify-center">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading users...</span>
            </div>
          </div>
        </div>
      ) : error ? (
        <div
          className="p-4 border border-red-300 bg-red-50 text-red-700 rounded-md 
                    animate-fade-in-down transform transition-all duration-300 ease-in-out opacity-0 translate-y-2"
          style={{ animationFillMode: "forwards" }}
        >
          <p>{error}</p>
        </div>
      ) : (
        <div
          className={cn(
            "transition-opacity duration-300",
            fadeInContent ? "opacity-100" : "opacity-0"
          )}
        >
          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {totalCount > 0 ? totalCount : rows.length}{" "}
                  {`${(totalCount > 0 ? totalCount : rows.length) !== 1 ? "users" : "user"}`}
                </Badge>
              </div>
              {selectedUsers.length > 0 ? (
                <Badge variant="outline" className="text-xs">
                  {selectedUsers.length} selected
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">No selection</span>
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="relative w-full max-w-md">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="h-10 w-full pl-8 pr-8"
                />
                {globalSearch && (
                  <button
                    type="button"
                    onClick={() => setGlobalSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {isTableLoading ? (
                <div className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Searching...</span>
                </div>
              ) : null}
            </div>

            <div className="relative">
              {isTableLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-md">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading...</span>
                  </div>
                </div>
              )}
              <div className={isTableLoading ? "pointer-events-none opacity-70" : ""}>
                <DataTable table={table}>
                  <DataTableToolbar table={table} />
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Right Panel — extracted to <UserDetailsSheet /> */}
      <UserDetailsSheet
        open={isUserDetailsPanelOpen}
        userId={currentUser?.id ?? null}
        fallbackName={currentUser?.name}
        initialTab={deepLinkTab}
        onOpenChange={(open) => {
          setIsUserDetailsPanelOpen(open);
          if (!open && (deepLinkUserId || deepLinkTab)) {
            const next = new URLSearchParams(searchParams);
            next.delete("userId");
            next.delete("tab");
            setSearchParams(next, { replace: true });
          }
        }}
        onEdit={() => currentUser && handleEditUser(currentUser)}
        onDelete={() => currentUser && handleDeleteUser(currentUser)}
        onResendCredentials={() => currentUser && confirmResendCredentials(currentUser)}
        onImpersonate={() => currentUser && setImpersonateTarget(currentUser)}
        canImpersonate={canImpersonate}
        resendingCredentials={resendingCredentialsForId === currentUser?.id}
        impersonating={impersonatingForId === currentUser?.id}
      />







      <EditUserDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        userId={currentUser?.id ?? null}
        fallbackName={currentUser?.name}
        fallbackEmail={currentUser?.email}
        fallbackCellPhone={currentUser?.cellPhone}
        fallbackProgram={currentUser?.program}
        onSaved={() => {
          setFadeInContent(false);
          refetchUsers();
        }}
      />

      {/* Add User Dialog */}
      <AddUserDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleCreateUserFromDialog}
        processing={processingAdd}
      />

      {/* Delete User Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={processingDelete}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={processingDelete}
              className="flex items-center gap-1"
            >
              {processingDelete ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete {selectedUsers.length} Users</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedUsers.length} selected
              users? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {deleteProgress && (
            <div className="my-4 animate-fade-in">
              <div className="flex justify-between mb-2">
                <span>Deleting users...</span>
                <span>
                  {deleteProgress.current} of {deleteProgress.total}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-primary h-2.5 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${
                      (deleteProgress.current / deleteProgress.total) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteDialogOpen(false)}
              disabled={deleteProgress !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmBulkDelete}
              disabled={deleteProgress !== null}
              className="flex items-center gap-1"
            >
              {deleteProgress !== null ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                "Delete All"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Users Dialog */}
      <Dialog
        open={isExportDialogOpen}
        onOpenChange={(open) => {
          if (!exporting) setIsExportDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Export students</DialogTitle>
            <DialogDescription>
              Download contact and enrollment details for analysis or follow-up.
              Includes phone, address, program, and related fields for matching
              students.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-1">
            <div className="space-y-3">
              <Label>Who to export</Label>
              <RadioGroup
                value={exportScope}
                onValueChange={(value) => setExportScope(value as StudentExportScope)}
                className="gap-3"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="all" id="export-all" />
                  <Label htmlFor="export-all" className="font-normal">
                    All students
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="enrolled" id="export-enrolled" />
                  <Label htmlFor="export-enrolled" className="font-normal">
                    Enrolled students only
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="not_enrolled" id="export-not-enrolled" />
                  <Label htmlFor="export-not-enrolled" className="font-normal">
                    Not enrolled students only
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="program" id="export-program" />
                  <Label htmlFor="export-program" className="font-normal">
                    Specific program
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {exportScope === "program" && (
              <div className="space-y-2">
                <Label htmlFor="export-program-select">Program</Label>
                <Select
                  value={exportProgram || undefined}
                  onValueChange={setExportProgram}
                  disabled={exportProgramsLoading}
                >
                  <SelectTrigger id="export-program-select">
                    <SelectValue
                      placeholder={
                        exportProgramsLoading
                          ? "Loading programs…"
                          : "Select a program"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {exportPrograms.map((program) => (
                      <SelectItem key={program} value={program}>
                        {program}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Format</Label>
              <RadioGroup
                value={exportFormat}
                onValueChange={(value) =>
                  setExportFormat(value as "csv" | "excel")
                }
                className="flex gap-4"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="csv" id="export-csv" />
                  <Label htmlFor="export-csv" className="font-normal">
                    CSV
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="excel" id="export-excel" />
                  <Label htmlFor="export-excel" className="font-normal">
                    Excel
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={exporting}
              onClick={() => setIsExportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={exporting || (exportScope === "program" && !exportProgram)}
              onClick={() => void handleExportStudents()}
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting…
                </>
              ) : (
                "Download"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog
        open={isImportDialogOpen}
        onOpenChange={(open) => {
          if (!importInProgress) setIsImportDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Import Users (Excel)</DialogTitle>
            <DialogDescription>
              Upload an .xlsx file with columns: First Name, Last Name, Email,
              Cell Phone Number, (optional) Password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="importFile">Excel file (.xlsx)</Label>
              <Input
                id="importFile"
                type="file"
                accept=".xlsx"
                onChange={handleImportFileChange}
                disabled={importInProgress}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="flex items-center"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Download template
              </Button>
              <div className="flex items-center gap-2 pt-1">
                <input
                  id="useBulkEndpoint"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={useBulkEndpoint}
                  onChange={(e) => {
                    setUseBulkEndpoint(e.target.checked);
                    // Re-run validation to enforce bulk constraints as needed
                    if (importRows.length) validateRows(importRows);
                  }}
                  disabled={importInProgress}
                />
                <Label htmlFor="useBulkEndpoint" className="text-sm">
                  Use admin bulk import endpoint (create/update students and
                  enrollments)
                </Label>
              </div>
            </div>

            {importRows.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Rows: {importValidation.total} • Valid:{" "}
                    {importValidation.valid} • Invalid:{" "}
                    {importValidation.invalid}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setImportRows([]);
                      setImportValidation({
                        total: 0,
                        valid: 0,
                        invalid: 0,
                        errors: [],
                      });
                    }}
                    disabled={importInProgress}
                  >
                    Clear
                  </Button>
                </div>

                {/* Review list of users to be imported (valid rows only) */}
                {validImportRows.length > 0 && (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>First Name</TableHead>
                          <TableHead>Last Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Cell Phone</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validImportRows.map((r, idx) => (
                          <TableRow key={`${r.email}-${idx}`}>
                            <TableCell>{r.firstName}</TableCell>
                            <TableCell>{r.lastName}</TableCell>
                            <TableCell>{r.email}</TableCell>
                            <TableCell>
                              {r.cellPhoneNumber}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Validation issues */}
                {importValidation.invalid > 0 && (
                  <div className="p-3 border rounded-md bg-amber-50 text-amber-900">
                    <div className="text-sm font-medium mb-1">
                      Some rows have issues
                    </div>
                    <ul className="text-sm list-disc pl-5 space-y-1 max-h-28 overflow-auto">
                      {importValidation.errors.slice(0, 5).map((e, i) => (
                        <li key={i}>
                          Row {e.index + 2} ({e.email || "no email"}):{" "}
                          {e.reasons.join(", ")}
                        </li>
                      ))}
                      {importValidation.errors.length > 5 && (
                        <li>+{importValidation.errors.length - 5} more…</li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Progress */}
                {importInProgress && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Importing…</span>
                      <span>
                        {importProgress.current} / {importProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all"
                        style={{
                          width: `${
                            importProgress.total
                              ? (importProgress.current /
                                  importProgress.total) *
                                100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Created: {importProgress.created} • Existing:{" "}
                      {importProgress.existed} • Failed: {importProgress.failed}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportDialogOpen(false)}
              disabled={importInProgress}
            >
              Cancel
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={importInProgress || validImportRows.length === 0}
                  className="flex items-center gap-1"
                >
                  Review & Confirm
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Import</AlertDialogTitle>
                  <AlertDialogDescription>
                    You are about to import {validImportRows.length} user(s).
                    This will create accounts for users that do not already
                    exist and will count existing emails as "existing".
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="rounded-md border max-h-72 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>First Name</TableHead>
                        <TableHead>Last Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Cell Phone</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {validImportRows.map((r, idx) => (
                        <TableRow key={`${r.email}-${idx}`}>
                          <TableCell>{r.firstName}</TableCell>
                          <TableCell>{r.lastName}</TableCell>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>
                            {r.cellPhoneNumber}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Back</AlertDialogCancel>
                  <AlertDialogAction onClick={handleStartImport}>
                    Confirm Import
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!impersonateTarget} onOpenChange={(open) => !open && setImpersonateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>View as user?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the student portal in a new tab signed in as{" "}
              <span className="font-medium">{impersonateTarget?.name || impersonateTarget?.email}</span>.
              Your admin session stays active in this tab. Actions are logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!impersonatingForId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleViewAsUser}
              disabled={!impersonateTarget || !!impersonatingForId}
            >
              {impersonatingForId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                "Open student portal"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resendCredentialsTarget} onOpenChange={(open) => !open && setResendCredentialsTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend student credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              This will generate a new temporary password and email it to the student.
              The student will be forced to reset their password on next login. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!resendingCredentialsForId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResendCredentials}
              disabled={!resendCredentialsTarget || !!resendingCredentialsForId}
            >
              {resendingCredentialsForId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Resend"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
